import type { CaptureSequence, CustomLandmarks } from '../types';

type Point = { x: number; y: number };

/** Keys that define the red-line segment in stored `custom` landmarks for each view. */
export type PelvisRedLinePinKey = 'ASIS' | 'PSIS' | 'L_ASIS' | 'R_ASIS' | 'L_PSIS' | 'R_PSIS';

export function getPelvisRedLinePinKeys(
  view: CaptureSequence,
  custom: CustomLandmarks | undefined
): [PelvisRedLinePinKey, PelvisRedLinePinKey] | null {
  if (!custom) return null;
  if (view === 'Anterior' && custom.L_ASIS && custom.R_ASIS) {
    return ['L_ASIS', 'R_ASIS'];
  }
  if (view === 'Posterior' && custom.L_PSIS && custom.R_PSIS) {
    return ['L_PSIS', 'R_PSIS'];
  }
  if ((view === 'Left' || view === 'Right') && custom.ASIS && custom.PSIS) {
    return ['ASIS', 'PSIS'];
  }
  return null;
}

/** Parameter t in [0,1] along segment A→B closest to P. */
export function closestPointOnSegmentT(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-6) return 0;
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  return Math.max(0, Math.min(1, t));
}

function sortByX(a: Point, b: Point): [Point, Point] {
  return a.x <= b.x ? [a, b] : [b, a];
}

/**
 * Returns a deterministic pelvis red-line segment for drawing.
 * This is draw-only normalization (no mutation of source custom landmark keys).
 */
export function getStablePelvisRedLine(
  view: CaptureSequence,
  custom: CustomLandmarks | undefined
): { start: Point; end: Point } | null {
  if (!custom) return null;

  if (view === 'Anterior') {
    const left = custom.L_ASIS;
    const right = custom.R_ASIS;
    if (!left || !right) return null;
    const [start, end] = sortByX(left, right);
    return { start, end };
  }

  if (view === 'Posterior') {
    const left = custom.L_PSIS;
    const right = custom.R_PSIS;
    if (!left || !right) return null;
    const [start, end] = sortByX(left, right);
    return { start, end };
  }

  if (view === 'Left' || view === 'Right') {
    const asis = custom.ASIS;
    const psis = custom.PSIS;
    if (!asis || !psis) return null;

    // In right view, anterior tends to larger x; in left view, anterior tends to smaller x.
    if (view === 'Right') {
      return asis.x >= psis.x
        ? { start: asis, end: psis }
        : { start: psis, end: asis };
    }
    return asis.x <= psis.x
      ? { start: asis, end: psis }
      : { start: psis, end: asis };
  }

  return null;
}

